import { t } from '../../i18n/init.js';
import type { XRSnapshot } from '../../xr/types.js';
import type { ComponentInstance } from '../component.js';
import { setHtmlIcon, setText } from '../dom-helpers.js';
import { ICON_CLOSE } from '../icons.js';

export interface XRModalProps {
  snapshot: XRSnapshot;
  embed: boolean;
}

export interface XRModalCallbacks {
  onClose: () => void;
  onEnterVR: () => void;
  onEnterAR: () => void;
}

const VR_ICON_SVG = `<svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20"><path d="M2 9 h20 l-2 9 H4 z" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/><path d="M7 9 V6 a5 5 0 0 1 10 0 v3" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>`;
const AR_ICON_SVG = `<svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2" fill="none"/><path d="M3 9 h18 M9 21 V9" stroke="currentColor" stroke-width="2" fill="none"/></svg>`;
const ARROW_RIGHT_SVG = `<svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16"><path d="M9 5 l7 7 -7 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

interface ActionButtonRefs {
  el: HTMLButtonElement;
  titleEl: HTMLHeadingElement;
  subtitleEl: HTMLElement;
}

function buildActionButton(
  iconSvg: string,
  iconBg: string,
  iconColor: string,
  title: string,
  subtitle: string,
  onClick: () => void,
): ActionButtonRefs {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.classList.add('viewer-ui-interactive');
  btn.classList.add('viewer-ui-tap-target');
  btn.style.cssText =
    'display:flex;width:100%;align-items:center;gap:0.75rem;padding:1rem;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);border-radius:1rem;cursor:pointer;text-align:left;transition:background 100ms,opacity 100ms';
  btn.addEventListener('mouseenter', () => {
    if (!btn.disabled) btn.style.background = 'rgba(255,255,255,0.08)';
  });
  btn.addEventListener('mouseleave', () => {
    btn.style.background = 'rgba(255,255,255,0.04)';
  });

  const iconWrap = document.createElement('span');
  iconWrap.setAttribute('aria-hidden', 'true');
  iconWrap.style.cssText = `display:flex;width:2.5rem;height:2.5rem;align-items:center;justify-content:center;border-radius:0.75rem;background:${iconBg};color:${iconColor};flex-shrink:0`;
  setHtmlIcon(iconWrap, iconSvg);
  btn.appendChild(iconWrap);

  const labelWrap = document.createElement('span');
  labelWrap.style.cssText = 'flex:1;display:flex;flex-direction:column;gap:0.125rem';
  const titleEl = document.createElement('h3');
  titleEl.style.cssText =
    'margin:0;font-size:0.875rem;font-weight:600;color:#fafafa;font-family:system-ui,sans-serif';
  setText(titleEl, title);
  labelWrap.appendChild(titleEl);
  const subtitleEl = document.createElement('span');
  subtitleEl.style.cssText =
    'font-size:0.625rem;color:#a1a1aa;font-family:ui-monospace,SFMono-Regular,Menlo,monospace';
  setText(subtitleEl, subtitle);
  labelWrap.appendChild(subtitleEl);
  btn.appendChild(labelWrap);

  const arrow = document.createElement('span');
  arrow.setAttribute('aria-hidden', 'true');
  arrow.style.cssText = 'color:#a1a1aa;display:flex;align-items:center';
  setHtmlIcon(arrow, ARROW_RIGHT_SVG);
  btn.appendChild(arrow);

  btn.addEventListener('click', onClick);
  return { el: btn, titleEl, subtitleEl };
}

function setButtonDisabled(btn: HTMLButtonElement, disabled: boolean, reason?: string): void {
  btn.disabled = disabled;
  btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  btn.style.opacity = disabled ? '0.5' : '1';
  btn.style.cursor = disabled ? 'not-allowed' : 'pointer';
  if (disabled && reason !== undefined) btn.title = reason;
  else btn.removeAttribute('title');
}

// Codex iter 5 plan addition: focus trap (Tab/Shift+Tab cycle) +
// autofocus ricalcolato post-detection + restore focus on close.
function getFocusableInOrder(refs: {
  closeBtn: HTMLButtonElement;
  vrBtn: HTMLButtonElement;
  arBtn: HTMLButtonElement;
}): readonly HTMLButtonElement[] {
  const all: HTMLButtonElement[] = [refs.closeBtn, refs.vrBtn, refs.arBtn];
  return all.filter((el) => !el.disabled);
}

function pickAutofocus(refs: {
  closeBtn: HTMLButtonElement;
  vrBtn: HTMLButtonElement;
  arBtn: HTMLButtonElement;
}): HTMLButtonElement {
  // Priority: VR (if enabled) → AR (if enabled) → close X (always enabled).
  if (!refs.vrBtn.disabled) return refs.vrBtn;
  if (!refs.arBtn.disabled) return refs.arBtn;
  return refs.closeBtn;
}

export function mountXRModal(
  root: HTMLElement,
  initial: XRModalProps,
  callbacks: XRModalCallbacks,
): ComponentInstance<XRModalProps> {
  const wrap = document.createElement('div');
  wrap.classList.add('viewer-ui-interactive');
  wrap.style.cssText =
    'position:fixed;inset:0;z-index:60;display:none;font-family:system-ui,sans-serif';

  const backdrop = document.createElement('div');
  backdrop.setAttribute('aria-hidden', 'true');
  backdrop.style.cssText =
    'position:absolute;inset:0;background:rgba(0,0,0,0.60);backdrop-filter:blur(4px)';
  wrap.appendChild(backdrop);

  const dialog = document.createElement('div');
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-labelledby', 'xr-modal-title');
  dialog.style.cssText =
    'position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:min(28rem,calc(100% - 2rem));background:rgba(15,15,17,0.92);backdrop-filter:blur(12px);border:1px solid rgba(255,255,255,0.08);border-radius:1.5rem;padding:2rem;box-shadow:0 20px 50px rgba(0,0,0,0.5);z-index:70';
  wrap.appendChild(dialog);

  // Close button (top-right)
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.classList.add('viewer-ui-interactive');
  closeBtn.classList.add('viewer-ui-tap-target');
  closeBtn.setAttribute('aria-label', t('viewer.xr.close_label'));
  closeBtn.style.cssText =
    'position:absolute;top:1rem;right:1rem;display:flex;width:2rem;height:2rem;align-items:center;justify-content:center;background:transparent;border:0;border-radius:9999px;color:#a1a1aa;cursor:pointer;transition:background 100ms';
  closeBtn.addEventListener('mouseenter', () => {
    if (!closeBtn.disabled) closeBtn.style.background = 'rgba(255,255,255,0.10)';
  });
  closeBtn.addEventListener('mouseleave', () => {
    closeBtn.style.background = 'transparent';
  });
  setHtmlIcon(closeBtn, ICON_CLOSE);
  closeBtn.addEventListener('click', () => {
    if (!closeBtn.disabled) callbacks.onClose();
  });
  dialog.appendChild(closeBtn);

  // Header icon
  const headerIcon = document.createElement('div');
  headerIcon.setAttribute('aria-hidden', 'true');
  headerIcon.style.cssText =
    'display:flex;width:3.5rem;height:3.5rem;align-items:center;justify-content:center;background:rgba(168,85,247,0.15);border-radius:1rem;margin-bottom:1.25rem;color:#d8b4fe';
  headerIcon.innerHTML = `<svg aria-hidden="true" viewBox="0 0 24 24" width="28" height="28"><rect x="2" y="7" width="20" height="10" rx="2" stroke="currentColor" stroke-width="2" fill="none"/><circle cx="8" cy="12" r="1.5" fill="currentColor"/><circle cx="16" cy="12" r="1.5" fill="currentColor"/></svg>`;
  dialog.appendChild(headerIcon);

  const title = document.createElement('h2');
  title.id = 'xr-modal-title';
  title.style.cssText =
    'margin:0 0 0.5rem 0;font-size:1.25rem;font-weight:300;letter-spacing:-0.01em;color:#fafafa';
  setText(title, t('viewer.xr.title'));
  dialog.appendChild(title);

  const desc = document.createElement('p');
  desc.style.cssText = 'margin:0 0 1.5rem 0;font-size:0.75rem;color:#a1a1aa;line-height:1.5';
  setText(desc, t('viewer.xr.desc'));
  dialog.appendChild(desc);

  const buttonsWrap = document.createElement('div');
  buttonsWrap.style.cssText =
    'display:flex;flex-direction:column;gap:0.75rem;margin-bottom:1.25rem';

  const vrRefs = buildActionButton(
    VR_ICON_SVG,
    'rgba(59,130,246,0.15)',
    '#93c5fd',
    t('viewer.xr.vr_title'),
    t('viewer.xr.vr_subtitle_ok'),
    () => {
      if (!vrRefs.el.disabled) callbacks.onEnterVR();
    },
  );
  buttonsWrap.appendChild(vrRefs.el);

  const arRefs = buildActionButton(
    AR_ICON_SVG,
    'rgba(16,185,129,0.15)',
    '#6ee7b7',
    t('viewer.xr.ar_title'),
    t('viewer.xr.ar_subtitle_ok'),
    () => {
      if (!arRefs.el.disabled) callbacks.onEnterAR();
    },
  );
  buttonsWrap.appendChild(arRefs.el);
  dialog.appendChild(buttonsWrap);

  // Inline error slot (status='error')
  const errorBox = document.createElement('div');
  errorBox.setAttribute('role', 'alert');
  errorBox.style.cssText =
    'display:none;margin-bottom:1rem;padding:0.625rem 0.875rem;background:rgba(239,68,68,0.10);border:1px solid rgba(239,68,68,0.20);border-radius:0.625rem;font-size:0.6875rem;color:#fca5a5;line-height:1.4';
  dialog.appendChild(errorBox);

  // Footer fallback. Default copy targets the desktop-without-HMD
  // case; in iframe contexts where Permissions Policy denies WebXR
  // (VR/AR both unsupported even when navigator.xr exists) the apply
  // path swaps to a more actionable instruction.
  const footer = document.createElement('p');
  footer.style.cssText =
    'margin:0;font-size:0.625rem;color:#a1a1aa;text-align:center;font-family:ui-monospace,SFMono-Regular,Menlo,monospace';
  dialog.appendChild(footer);

  const renderFooter = (props: XRModalProps): void => {
    // 4.M (Codex iter 3 plan MAJOR fix): predicate must catch the
    // real iframe-without-allow case where navigator.xr is present
    // but isSessionSupported() rejects for both modes. Checking
    // !hasWebXR alone would miss this. We render the embed tip
    // whenever both modes are unavailable while embedded.
    const showEmbedXRTip =
      props.embed && !props.snapshot.support.vrSupported && !props.snapshot.support.arSupported;
    if (showEmbedXRTip) {
      footer.innerHTML = '';
      const lead = document.createElement('span');
      setText(lead, t('viewer.xr.embed_tip_lead'));
      footer.appendChild(lead);
      const code = document.createElement('strong');
      code.style.cssText = 'font-weight:600;color:#fde68a';
      setText(code, 'allow="xr-spatial-tracking"');
      footer.appendChild(code);
      const tail = document.createElement('span');
      setText(tail, t('viewer.xr.embed_tip_tail'));
      footer.appendChild(tail);
    } else {
      footer.innerHTML = '';
      setText(footer, t('viewer.xr.footer_no_hmd'));
    }
  };

  // Codex iter 5 plan addition: focus trap. Intercept Tab + Shift+Tab
  // and cycle within the visible focusable elements (close + enabled
  // action buttons). Esc handled by orchestrator's global ESC chain
  // (so we don't double-handle here).
  const onKeyDown = (ev: KeyboardEvent): void => {
    if (ev.key !== 'Tab') return;
    const focusables = getFocusableInOrder({
      closeBtn,
      vrBtn: vrRefs.el,
      arBtn: arRefs.el,
    });
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (first === undefined || last === undefined) return;
    const active = document.activeElement;
    if (ev.shiftKey) {
      if (active === first || !dialog.contains(active)) {
        ev.preventDefault();
        last.focus();
      }
    } else {
      if (active === last || !dialog.contains(active)) {
        ev.preventDefault();
        first.focus();
      }
    }
  };
  dialog.addEventListener('keydown', onKeyDown);

  root.appendChild(wrap);

  // Track previous open state so we apply autofocus only on open AND
  // re-pick when capability changes (post-detection update).
  let prevOpen = false;
  let prevVrEnabled = false;
  let prevArEnabled = false;

  const applyDisabled = (snapshot: XRSnapshot): void => {
    const starting = snapshot.status === 'starting';
    const vrAvailable = snapshot.support.vrSupported && !starting;
    const arAvailable = snapshot.support.arSupported && !starting;
    setButtonDisabled(
      vrRefs.el,
      !vrAvailable,
      starting
        ? t('viewer.xr.starting')
        : !snapshot.support.vrSupported
          ? t('viewer.xr.vr_unsupported')
          : undefined,
    );
    setButtonDisabled(
      arRefs.el,
      !arAvailable,
      starting
        ? t('viewer.xr.starting')
        : !snapshot.support.arSupported
          ? t('viewer.xr.ar_unsupported')
          : undefined,
    );
    setButtonDisabled(closeBtn, starting, starting ? t('viewer.xr.starting') : undefined);
  };

  const apply = (props: XRModalProps): void => {
    const open = props.snapshot.modalOpen;
    wrap.style.display = open ? 'block' : 'none';
    if (!open) {
      prevOpen = false;
      return;
    }
    applyDisabled(props.snapshot);
    renderFooter(props);

    // Inline error visibility
    if (props.snapshot.status === 'error' && props.snapshot.lastError !== null) {
      errorBox.style.display = 'block';
      setText(errorBox, props.snapshot.lastError);
    } else {
      errorBox.style.display = 'none';
      setText(errorBox, '');
    }

    // Subtitle hints when unsupported (replaces default device list)
    if (!props.snapshot.support.vrSupported && props.snapshot.support.hasWebXR) {
      setText(vrRefs.subtitleEl, t('viewer.xr.vr_no_hmd'));
    } else if (!props.snapshot.support.hasWebXR) {
      setText(vrRefs.subtitleEl, t('viewer.xr.webxr_unavailable'));
    } else {
      setText(vrRefs.subtitleEl, t('viewer.xr.vr_subtitle_ok'));
    }
    if (!props.snapshot.support.arSupported && props.snapshot.support.hasWebXR) {
      setText(arRefs.subtitleEl, t('viewer.xr.ar_no_hmd'));
    } else if (!props.snapshot.support.hasWebXR) {
      setText(arRefs.subtitleEl, t('viewer.xr.webxr_unavailable'));
    } else {
      setText(arRefs.subtitleEl, t('viewer.xr.ar_subtitle_ok'));
    }

    // Autofocus: on open transition, OR when capability flips from
    // disabled to enabled (post-detection promotion). Don't steal
    // focus on every snapshot tick.
    const vrEnabled = !vrRefs.el.disabled;
    const arEnabled = !arRefs.el.disabled;
    const capabilityFlipped = vrEnabled !== prevVrEnabled || arEnabled !== prevArEnabled;
    if (!prevOpen || capabilityFlipped) {
      const target = pickAutofocus({
        closeBtn,
        vrBtn: vrRefs.el,
        arBtn: arRefs.el,
      });
      try {
        target.focus();
      } catch {
        // happy-dom may reject focus on disabled-then-enabled mid-tick — ignore
      }
    }
    prevOpen = true;
    prevVrEnabled = vrEnabled;
    prevArEnabled = arEnabled;
  };

  apply(initial);

  return {
    update(props: XRModalProps) {
      apply(props);
    },
    cleanup() {
      dialog.removeEventListener('keydown', onKeyDown);
      wrap.remove();
    },
  };
}
